/**
 * Wraps an async handler so only one call runs at a time: calls arriving while
 * one is in flight are dropped, not queued.
 *
 * For tap handlers that create something server-side. A double tap on a
 * primary button is ordinary user behavior, and an awaited write leaves a wide
 * window for the second one to land, which is how one tap turns into two
 * dialogs, two openers and two lessons counted.
 *
 * The lock is held until the call actually settles, with no expiry, and that
 * is deliberate. An RTDB write resolves on server acknowledgement, so offline
 * it stays pending until the connection returns while the write sits queued
 * locally. Releasing the lock on a timer would let the next tap queue a SECOND
 * write behind the first, and both would commit on reconnect: exactly the
 * duplicate this guard exists to prevent, only harder to spot.
 *
 * The cost is that the control is inert while a write is queued. That resolves
 * itself: the queued write commits on reconnect, the promise settles, the lock
 * releases and the handler completes. The lock also lives in the caller's
 * setup() scope, so leaving the screen and coming back starts a fresh one. An
 * inert button that heals is a better trade than a duplicated lesson that does
 * not.
 *
 * @param {(...args: any[]) => Promise<any>} fn
 * @returns {(...args: any[]) => Promise<any>} resolves to undefined when dropped
 */
export function singleFlight(fn) {
  let inFlight = false;
  return async (...args) => {
    if (inFlight) return undefined;
    inFlight = true;
    try {
      return await fn(...args);
    } finally {
      inFlight = false;
    }
  };
}
